// Alteration service — garment-aware options + measurement fields, wired to
// the boutique's REAL Shopify "Alteration Service" product (variants matched
// by name, so prices always follow Shopify). User's live variant list:
//   Add sleeves $10 · Blouse side fitting $10 · Zipper blouse fitting $15
//   Kurti side fitting $10 · Side cut kurti length $15 · Long kurti/gown $35
//   Lehenga length $35 · Fall pico $25 · Cup removal $5 · Can-can removal $15

import { api } from "@/lib/api";

export const ALTERATION_PRODUCT_SLUG = "alteration-service";

// Measurement field definitions (values in inches)
export const MEASUREMENT_FIELDS = {
  chest: "Chest",
  lower_bust: "Lower bust",
  arm: "Arm circumference",
  waist: "Waist",
  hip: "Hip",
  length: "Length you want",
  sleeve_length: "Sleeve length",
};

// Each option carries `match`: a lowercase substring of the Shopify variant
// title used to resolve the charge variant. price is display-only truth.
const OPT = {
  addSleeves: { id: "add-sleeves", label: "Add sleeves", price: 10, match: "add sleeves", fields: ["arm", "sleeve_length"] },
  sideFittingBlouse: { id: "side-fitting", label: "Side fitting", price: 10, match: "blouse side", fields: ["chest", "lower_bust", "arm"] },
  sideFittingKurti: { id: "side-fitting", label: "Side fitting", price: 10, match: "kurti side", fields: ["chest", "waist", "hip"] },
  zipper: { id: "zipper-fitting", label: "Zipper fitting (blouse)", price: 15, match: "zipper", fields: ["chest", "lower_bust"] },
  cupBlouse: { id: "cup-removal-blouse", label: "Cup removal", price: 5, match: "cup removal", fields: [] },
  cupLehenga: { id: "cup-removal-lehenga", label: "Cup removal (blouse)", price: 5, match: "cup removal", fields: [] },
  lengthSideCut: { id: "length-sidecut", label: "Length — side cut", price: 15, match: "side cut", fields: ["length"] },
  lengthLong: { id: "length-long", label: "Length — long kurti / gown", price: 35, match: "long kurti", fields: ["length"] },
  lehengaLength: { id: "lehenga-length", label: "Lehenga length", price: 35, match: "lehenga length", fields: ["length"] },
  cancan: { id: "cancan-removal", label: "Can-can removal", price: 15, match: "can-can", fields: [] },
  fallPico: { id: "fall-pico", label: "Fall & pico", price: 25, match: "fall", fields: [] },
  other: { id: "other", label: "Other — we'll confirm the price on WhatsApp", price: null, match: null, fields: [] },
};

// Garment-aware option lists (per the boutique's own service list)
export const alterationOptionsFor = (product) => {
  const cols = (product?.collections || []).map((c) => (c || "").toLowerCase());
  const has = (x) => cols.some((c) => c.includes(x));
  let opts;
  if (has("lehenga"))
    opts = [OPT.lehengaLength, OPT.cancan, { ...OPT.sideFittingBlouse, label: "Blouse fitting" }, OPT.zipper, OPT.cupLehenga, OPT.addSleeves];
  else if (has("blouse"))
    opts = [OPT.addSleeves, OPT.cupBlouse, OPT.sideFittingBlouse, OPT.zipper];
  else if (has("saree"))
    opts = [OPT.fallPico];
  else if (has("kurti") || has("kurta") || has("sharara") || has("anarkali"))
    opts = [OPT.sideFittingKurti, OPT.addSleeves, OPT.lengthSideCut, OPT.lengthLong];
  else
    opts = [OPT.sideFittingKurti, OPT.addSleeves, OPT.lengthLong];
  return [...opts, OPT.other];
};

// Show the alteration option only on women's wear (not men/kids/jewellery).
// Match on the collection HANDLE prefix — a substring check would match
// "women" against "men" (learned the hard way).
const ALT_EXCLUDE_RE = /^(men|mens|kids?|jewelry|jewellery|accessor(y|ies))(-|$)/;
export const alterationEligible = (product) =>
  !(product?.collections || []).some((c) => ALT_EXCLUDE_RE.test((c || "").toLowerCase()));

// Load the charge product's variants (cached). null = not synced yet.
let _variants;
export const getAlterationVariants = async () => {
  if (_variants !== undefined) return _variants;
  try {
    const { data } = await api.get(`/products/${ALTERATION_PRODUCT_SLUG}`);
    const prod = data?.product || data;
    const list = (prod?.variants || [])
      .map((v) => ({ title: (v.size || v.title || "").toLowerCase(), price: v.price, variant_id: v.variant_id }))
      .filter((v) => v.title && v.variant_id);
    _variants = list.length ? list : null;
  } catch {
    _variants = null;
  }
  return _variants;
};

// Attach the Shopify variant id to each priced option via name match.
export const attachVariantIds = (options, variants) =>
  options.map((o) => ({
    ...o,
    variantId:
      o.price == null || !variants
        ? null
        : variants.find((v) => v.title.includes(o.match))?.variant_id || null,
  }));

export const altPriceLabel = (price) => (price == null ? "TBD" : `+$${price}`);
